import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchFeatureFlags, fetchPublicFlag, toggleFeatureFlag } from "@/services/featureFlags";
import type { FeatureFlag } from "@/services/featureFlags";

export type { FeatureFlag };

const FEATURE_FLAGS_KEY = "feature-flags";

/**
 * DB-backed feature flags (super-admin only). The flags live in the `app`
 * schema owned by the NestJS API (app.feature_flags) and are read/toggled via
 * the super-admin-guarded /feature-flags endpoints. Platform-global — no
 * company filter.
 */
export function useFeatureFlags() {
  return useQuery<FeatureFlag[]>({
    queryKey: [FEATURE_FLAGS_KEY],
    queryFn: fetchFeatureFlags,
    // Intentionally aligned with the server-side flag cache
    // (FeatureFlagsService CACHE_TTL_MS = 30_000). Keep the two in sync: a
    // longer staleTime here would let the UI lag a toggle the API already sees.
    staleTime: 30_000,
  });
}

/**
 * Toggle a flag on/off. Invalidates the list on success so the table reflects
 * the new state (and the updated_at / updated_by audit fields).
 */
export function useToggleFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      toggleFeatureFlag(key, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FEATURE_FLAGS_KEY] });
    },
  });
}

/**
 * An allow-listed flag readable before sign-in (see fetchPublicFlag). `data`
 * is undefined while loading — callers render a spinner until it resolves so
 * a page never flashes the wrong flow. Fail-closed to false.
 */
export function usePublicFeatureFlag(key: string) {
  return useQuery<boolean>({
    queryKey: ["public-feature-flag", key],
    queryFn: () => fetchPublicFlag(key),
    staleTime: 30_000,
  });
}
